import { cn } from '@/lib/utils';
import type { DataTableCellTone } from '@shared/types/views';
import { ToneDot } from '@/components/views/atoms/ToneDot';
interface DataTableRendererProps {
  rows: Array<Record<string, unknown>>;
  columns?: string[];
  columnLabels?: Record<string, string>;
  /** Canonical view columns are contractual, including fields whose values are missing. */
  preserveDeclaredColumns?: boolean;
  onRowClick?: (row: Record<string, unknown>) => void;
  /** Optional action shown as the first column, kept visible when the table
   * scrolls horizontally. Its click never triggers onRowClick. */
  rowAction?: { label: string; onClick: (row: Record<string, unknown>) => void };
  /** Sortable headers: the current sort and what a header click asks for. */
  sort?: { key: string; direction: 'asc' | 'desc' } | null;
  onSort?: (column: string) => void;
  /** Reading of a cell (colored dot), when the producer gave one. */
  cellTone?: (row: Record<string, unknown>, column: string) => DataTableCellTone | undefined;
}

const isUrl = (value: string): boolean => /^https?:\/\//i.test(value);
const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isTemporalColumn = (columnKey: string): boolean =>
  /(^|_)(date|time|created|updated|published|started|ended|at)($|_)/i.test(columnKey);

const CRM_STAGE_LABELS: Record<string, string> = {
  appointmentscheduled: 'Rendez-vous planifié',
  qualifiedtobuy: 'Opportunité qualifiée',
  presentationscheduled: 'Présentation planifiée',
  decisionmakerboughtin: 'Décideur engagé',
  contractsent: 'Contrat envoyé',
  closedwon: 'Gagnée',
  closedlost: 'Perdue',
};

export const formatBusinessTableValue = (value: unknown, columnKey: string): unknown => {
  if (typeof value !== 'string' || !/(^|_)(stage|status)($|_)/i.test(columnKey)) return value;
  const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, '');
  return CRM_STAGE_LABELS[normalized] ?? value;
};

const isMetadataOnlyText = (value: string): boolean => {
  const lines = value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return (
    lines.length > 0 &&
    lines.every((line) => /^(title|url|published|author):/i.test(line))
  );
};

const hasUsefulValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value !== 'string') return true;
  const trimmed = value.trim();
  return trimmed.length > 0 && !isMetadataOnlyText(trimmed);
};

const formatColumnLabel = (column: string): string => {
  return column
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase());
};

const getVisibleColumnLabel = (
  column: string,
  columnLabels?: Record<string, string>
): string => {
  const label = columnLabels?.[column] ?? formatColumnLabel(column);
  return column.toLowerCase() === 'url' && label.toLowerCase() === 'url'
    ? 'Site'
    : label;
};

const toPreviewText = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `${value.length} éléments`;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const CellRenderer = ({
  value,
  columnKey,
}: {
  value: unknown;
  columnKey: string;
}) => {
  value = formatBusinessTableValue(value, columnKey);
  if (value === null || value === undefined) {
    return <span className="text-stone-400">—</span>;
  }

  if (typeof value === 'string' && isUrl(value)) {
    let label = value.replace(/^https?:\/\//i, '').replace(/\/$/, '');
    try {
      label = new URL(value).hostname.replace(/^www\./i, '');
    } catch {
      // Keep the readable URL fallback.
    }
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        title={value}
        className="block max-w-[220px] truncate text-domino-prune underline hover:text-domino-aubergine"
      >
        {label}
      </a>
    );
  }

  if (typeof value === 'string' && isEmail(value)) {
    return (
      <a href={`mailto:${value}`} className="text-domino-prune underline hover:text-domino-aubergine">
        {value}
      </a>
    );
  }

  if (typeof value === 'string' && isTemporalColumn(columnKey)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return (
        <span title={value} className="whitespace-nowrap text-domino-inkSoft">
          {date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      );
    }
  }

  if (typeof value === 'string' && isMetadataOnlyText(value)) {
    return <span className="text-stone-400">—</span>;
  }

  if (typeof value === 'number') {
    return (
      <span className="block text-right font-medium tabular-nums">
        {value.toLocaleString('fr-FR')}
      </span>
    );
  }

  if (typeof value === 'boolean') {
    return <span>{value ? 'Oui' : 'Non'}</span>;
  }

  if (Array.isArray(value)) {
    const visibleEntries = value.slice(0, 3).map(toPreviewText).join(', ');
    return <span>{value.length > 3 ? `${visibleEntries} +${value.length - 3}` : visibleEntries}</span>;
  }

  if (typeof value === 'object') {
    const preview = toPreviewText(value);
    const count = Object.keys(value as Record<string, unknown>).length;
    return (
      <span className="cursor-help text-domino-inkSoft" title={preview}>
        {count} champs
      </span>
    );
  }

  // Long text wraps on two lines instead of widening the table; the full
  // value stays in the tooltip and in the row's detailed fiche.
  const text = String(value);
  return (
    <span className="line-clamp-2 min-w-[8rem] max-w-[260px] whitespace-normal" title={text}>
      {text}
    </span>
  );
};

export const DataTableRenderer = ({
  rows,
  columns,
  columnLabels,
  preserveDeclaredColumns = false,
  onRowClick,
  rowAction,
  sort,
  onSort,
  cellTone,
}: DataTableRendererProps) => {
  if (rows.length === 0) {
    return (
      <div className="text-sm text-domino-inkSoft">
        Aucune donnée.
      </div>
    );
  }

  const candidateColumns =
    columns && columns.length > 0
      ? preserveDeclaredColumns ? columns : columns.filter((column) =>
          rows.some((row) => Object.prototype.hasOwnProperty.call(row, column))
        )
      : Object.keys(rows[0] ?? {}).filter(
          (key) => key !== 'id' && !key.startsWith('_')
        );
  const resolvedColumns = preserveDeclaredColumns && columns?.length
    ? candidateColumns
    : candidateColumns.filter((column) => rows.some((row) => hasUsefulValue(row[column])));

  if (resolvedColumns.length === 0) {
    return (
      <div className="text-sm text-domino-inkSoft">
        Aucun champ exploitable.
      </div>
    );
  }

  return (
    <div data-view-surface className="overflow-x-auto rounded-lg border border-domino-cardBorder bg-domino-warm">
      <table className="min-w-full divide-y divide-domino-cardBorder">
        <thead className="bg-domino-muted">
          <tr>
            {rowAction && <th className="px-4 py-2" aria-label="Actions" />}
            {resolvedColumns.map((column, columnIndex) => (
              <th
                key={column}
                aria-sort={sort?.key === column ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                className={cn('px-4 py-2 text-left text-xs font-medium text-domino-inkSoft',
                  columnIndex === 0 && !rowAction && 'sticky left-0 z-[1] bg-domino-muted')}
              >
                {onSort ? (
                  <button type="button" onClick={() => onSort(column)}
                    className="inline-flex items-center gap-1 text-left hover:text-domino-ink">
                    {getVisibleColumnLabel(column, columnLabels)}
                    <span aria-hidden="true" className={sort?.key === column ? 'text-domino-ink' : 'text-domino-cardBorder'}>
                      {sort?.key === column && sort.direction === 'desc' ? '↓' : '↑'}
                    </span>
                  </button>
                ) : getVisibleColumnLabel(column, columnLabels)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-domino-border">
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              data-row-id={typeof row.id === 'string' ? row.id : undefined}
              className={cn('group', onRowClick ? 'cursor-pointer hover:bg-domino-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-domino-aubergine' : 'hover:bg-domino-muted')}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick(row);
                }
              } : undefined}
            >
              {rowAction && <td className="px-4 py-2 align-top">
                <button
                  type="button"
                  className="whitespace-nowrap rounded-md border border-domino-cardBorder bg-domino-white px-2 py-1 text-xs font-medium text-domino-aubergine hover:border-domino-aubergine"
                  onClick={(event) => { event.stopPropagation(); rowAction.onClick(row); }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {rowAction.label}
                </button>
              </td>}
              {resolvedColumns.map((column, columnIndex) => (
                <td key={column} className={cn('px-4 py-2 align-top text-sm text-domino-ink',
                  // The element's name stays in view when the table scrolls sideways.
                  columnIndex === 0 && !rowAction && 'sticky left-0 z-[1] bg-domino-warm font-medium group-hover:bg-domino-muted')}>
                  {(() => {
                    const tone = cellTone?.(row, column);
                    return tone ? (
                      <span className="inline-flex items-start gap-1.5"><ToneDot tone={tone} className="mt-1.5" /><CellRenderer value={row[column]} columnKey={column} /></span>
                    ) : <CellRenderer value={row[column]} columnKey={column} />;
                  })()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-domino-cardBorder px-4 py-2 text-xs text-domino-inkSoft">
        {rows.length} résultat{rows.length > 1 ? 's' : ''}
      </div>
    </div>
  );
};
